import AuthActionHandler from 'pages/AuthActionHandler'
import AccountPage from 'pages/authenticated/Account'
import AdminConsole from 'pages/authenticated/AdminConsole'
import { useAuth } from 'pages/authenticated/AuthContext'
import FavoritesPage from 'pages/authenticated/Favorites'
import MessagesPage from 'pages/authenticated/Messages'
import MyListing from 'pages/authenticated/MyListing'
import OwnerListingManagerPage from 'pages/authenticated/OwnerListingManager'
import ProfilePage from 'pages/authenticated/Profile'
import SupportConsole from 'pages/authenticated/SupportConsole'
import TenantArea from 'pages/authenticated/TenantArea'
import AuthUserPage from 'pages/AuthUser'
import ConfirmEmailPage from 'pages/ConfirmEmail'
import ConfirmSearchAlertPage from 'pages/ConfirmSearchAlert'
import ContactPage from 'pages/Contact'
import CreateAdPage from 'pages/CreateAd'
import CreateAdOwnerRegisterPage from 'pages/CreateAdOwnerRegister'
import CreateAdPublishedPage from 'pages/CreateAdPublished'
import CreateProfilePage from 'pages/CreateProfile'
import Forbidden from 'pages/Forbidden'
import Home from 'pages/Home'
import ListingPage from 'pages/Listing'
import ListingsPage from 'pages/ListingsPage'
import PostRegisterChoicePage from 'pages/PostRegisterChoice'
import RegisterPropertyPage from 'pages/RegisterProperty'
import RegisterUserPage from 'pages/RegisterUser'
import ResetPassword from 'pages/ResetPassword'
import ResetPasswordAction from 'pages/ResetPasswordAction'
import TermsOfUsePage from 'pages/TermsOfUse'
import VerifyEmailPending from 'pages/VerifyEmailPending'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Routes as ReactRoutes, Route, Navigate, useLocation } from 'react-router-dom'

import PageLoading from '../components/common/loading/PageLoading'
import Header from '../components/header/Header'
import Footer from '../components/layout/Footer'
import { scrollAppToTop } from '../utils/scrollAppToTop'
import GuestOnlyRoute from './GuestOnlyRoute'
import ProtectedRoute from './ProtectedRoute'
import RequireOwnerEmailVerified from './RequireOwnerEmailVerified'
import RoleGuard from './RoleGuard'

const Routes = () => {
  const location = useLocation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { pathname } = location
  const { validateToken } = useAuth()
  const [authChecked, setAuthChecked] = useState(false)
  useEffect(() => {
    const checkAuth = async () => {
      await validateToken()
      setAuthChecked(true)
    }

    checkAuth()
  }, [])

  useLayoutEffect(() => {
    scrollAppToTop()
  }, [pathname, location.key])

  if (!authChecked) {
    return (
      <div className="min-h-screen flex flex-col bg-imovWhiteShadow">
        <Header />
        <PageLoading minHeightClassName="min-h-0 h-full" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-imovWhiteShadow">
      <Header />
      <div ref={scrollContainerRef} data-main-scroll className="min-h-0 flex-1 overflow-y-auto">
        <ReactRoutes>
          <Route path="/" element={<Home />} />
          <Route path="/listings" element={<ListingsPage />} />
          <Route path="/alerts/confirm" element={<ConfirmSearchAlertPage />} />
          <Route path="/email/confirm" element={<ConfirmEmailPage />} />
          <Route path="/email/confirm/:token" element={<ConfirmEmailPage />} />
          <Route
            path="/verify-email"
            element={
              <ProtectedRoute>
                <VerifyEmailPending />
              </ProtectedRoute>
            }
          />
          <Route path="/create-ad" element={<CreateAdPage />} />
          <Route
            path="/create-ad/register-owner"
            element={<CreateAdOwnerRegisterPage />}
          />
          <Route path="/create-ad/published" element={<CreateAdPublishedPage />} />
          <Route path="/create-profile" element={<CreateProfilePage />} />
          <Route path="/register-property" element={<RegisterPropertyPage />} />
          <Route
            path="/create-user"
            element={
              <GuestOnlyRoute>
                <RegisterUserPage />
              </GuestOnlyRoute>
            }
          />
          <Route path="/post-register-choice" element={<PostRegisterChoicePage />} />
          <Route
            path="/auth"
            element={
              <GuestOnlyRoute>
                <AuthUserPage />
              </GuestOnlyRoute>
            }
          />
          <Route path="/listing/:id" element={<ListingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/forbidden" element={<Forbidden />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password-action" element={<ResetPasswordAction />} />
          <Route path="/auth/action" element={<AuthActionHandler />} />
          <Route path="/__/auth/action" element={<AuthActionHandler />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <RequireOwnerEmailVerified>
                  <AccountPage />
                </RequireOwnerEmailVerified>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <RequireOwnerEmailVerified>
                  <ProfilePage />
                </RequireOwnerEmailVerified>
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-listing/:id"
            element={
              <ProtectedRoute>
                <RequireOwnerEmailVerified>
                  <MyListing />
                </RequireOwnerEmailVerified>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-listings/:id/manage"
            element={
              <ProtectedRoute>
                <RequireOwnerEmailVerified>
                  <OwnerListingManagerPage />
                </RequireOwnerEmailVerified>
              </ProtectedRoute>
            }
          />
          <Route path="/owner-console" element={<Navigate to="/account" replace />} />
          <Route
            path="/tenant-area"
            element={
              <ProtectedRoute>
                <RoleGuard roles={['tenant', 'admin']}>
                  <TenantArea />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/support-console"
            element={
              <ProtectedRoute>
                <RoleGuard roles={['support', 'admin']}>
                  <SupportConsole />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-console"
            element={
              <ProtectedRoute>
                <RoleGuard roles={['admin']}>
                  <AdminConsole />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
        </ReactRoutes>
        {pathname !== '/create-ad' ? (
          <div className="mt-8 bg-imovWhite">
            <Footer />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default Routes
